import OldTariffCard from "./OldTariffCard";
import TariffCard from "./TariffCard";
import compact from "lodash/compact";
import first from "lodash/first";
import { FC, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { AutoSizer } from "react-virtualized";
import {
  DealershipType,
  TariffOutput,
  useGetOrganizationsQuery,
  useGetTariffsByOidQuery,
} from "~@/graphql/codegen/generated";
import { useAppDispatch } from "~@/store";
import { selectTariff } from "~@/store/commons/pricing.slice";

type TariffsListProps = {
  isSettings?: boolean;
};

const TariffsList: FC<TariffsListProps> = ({ isSettings }) => {
  const { data: organizationsData } = useGetOrganizationsQuery({
    skip: !isSettings,
  });

  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const currentOrganization = useMemo(() => {
    return organizationsData?.getOrganizations?.current;
  }, [organizationsData]);

  const { data: tariffsData, loading: areTariffsLoading } =
    useGetTariffsByOidQuery();

  const tariffs = useMemo<TariffOutput[]>(() => {
    return compact(tariffsData?.getTariffsByOID);
  }, [tariffsData]);

  const renderTariffs = useCallback(() => {
    return tariffs.map((tariff) => {
      if (currentOrganization?.dealershipType === DealershipType.Old) {
        return (
          <OldTariffCard
            key={tariff.id}
            {...tariff}
            isCurrent={
              tariff.id === currentOrganization?.tariffId &&
              !currentOrganization.isCanceled
            }
            defaultChecked={tariff.id === currentOrganization?.newTariffId}
          />
        );
      }

      return (
        <TariffCard
          key={tariff.id}
          {...tariff}
          name={
            tariff.name.includes("standard")
              ? t(`tariff.${tariff.name}`)
              : tariff.name
          }
          isCurrent={
            tariff.id === currentOrganization?.tariffId &&
            !currentOrganization.isCanceled
          }
          isOnly={tariffs.length === 1}
          defaultChecked={tariff.id === currentOrganization?.newTariffId}
        />
      );
    });
  }, [currentOrganization, tariffs, t]);

  useEffect(() => {
    if (tariffs.length > 1 || isSettings) return;

    const tariff = first(tariffs);

    dispatch(selectTariff(tariff ?? null));
  }, [currentOrganization, tariffs, dispatch, isSettings]);

  return areTariffsLoading ? (
    <Skeleton
      count={3}
      height={117}
      inline
      containerClassName="tariffs-list tariffs-list--is-loading"
      baseColor="#f8f8f8"
      highlightColor="#fcfcfc"
      borderRadius={16}
      wrapper={({ children }) => (
        <div style={{ marginBottom: 20, lineHeight: 1 }}>{children}</div>
      )}
    />
  ) : (
    <AutoSizer disableHeight>
      {({ width }) => (
        <div className="tariffs-list" style={{ width }}>
          {renderTariffs()}
        </div>
      )}
    </AutoSizer>
  );
};

export default TariffsList;
